import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { isLikelyWebView } from '../../utils/pushNotifications';
import './ScholarshipQuizPrompt.css';

const unwrap = (response) => response?.data?.data || response?.data?.records || response?.data || [];
const serverBase = (process.env.REACT_APP_API_URL || 'http://localhost:5030/api').replace(/\/api\/?$/, '');
const mediaUrl = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const url = /^(https?:|data:|blob:)/i.test(raw)
    ? raw
    : `${serverBase}${raw.startsWith('/') ? '' : '/'}${raw}`;
  if (/^(data:|blob:)/i.test(url)) return url;
  const token = localStorage.getItem('token');
  if (!token || /[?&]t=/.test(url)) return url;
  return `${url}${url.includes('?') ? '&' : '?'}t=${encodeURIComponent(token)}`;
};

export default function ScholarshipQuizPrompt() {
  const [scholarships, setScholarships] = useState([]);
  const [selected, setSelected] = useState(null);
  const [attempt, setAttempt] = useState(null);
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState({});
  const [questionIndex, setQuestionIndex] = useState(0);
  const [startedAt, setStartedAt] = useState(0);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    let active = true;
    api.get('/quiz/scholarship/available')
      .then((response) => {
        if (!active) return;
        const records = Array.isArray(unwrap(response)) ? unwrap(response) : [];
        setScholarships(records);
        const pending = records.find((item) => item.attemptStatus !== 'completed' && !item.award);
        const promptKey = pending ? `scholarship_prompted_${pending._id}` : '';
        if (pending && isLikelyWebView() && !sessionStorage.getItem(promptKey)) {
          sessionStorage.setItem(promptKey, '1');
          setSelected(pending);
        }
      })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  if (!scholarships.length) return null;

  const courseName = selected?.course?.courseName || selected?.course?.courseCode || 'selected course';
  const questions = quiz?.questions || [];
  const question = questions[questionIndex];

  const openScholarship = (item) => {
    setSelected(item);
    setAttempt(null);
    setQuiz(null);
    setAnswers({});
    setQuestionIndex(0);
    setStartedAt(0);
    setResult(null);
    setMessage('');
  };

  const closeDialog = () => {
    if (busy) return;
    setSelected(null);
    setAttempt(null);
    setQuiz(null);
    setAnswers({});
    setQuestionIndex(0);
    setResult(null);
    setMessage('');
  };

  const startQuiz = async () => {
    if (!selected?._id) return;
    setBusy(true);
    setMessage('');
    try {
      const response = await api.post(`/quiz/${selected._id}/attempt`);
      const payload = response.data?.data || response.data;
      setAttempt(payload.attempt);
      setQuiz(payload.quiz);
      setAnswers({});
      setQuestionIndex(0);
      setStartedAt(Date.now());
      setResult(null);
    } catch (error) {
      setMessage(error.response?.data?.message || 'Unable to start the scholarship quiz.');
    } finally {
      setBusy(false);
    }
  };

  const submitQuiz = async () => {
    if (!attempt?._id || !quiz?._id) return;
    setBusy(true);
    setMessage('');
    try {
      const submittedAnswers = Object.entries(answers).map(([questionId, value]) => ({
        questionId,
        selectedOptionId: value.optionId || null,
        textAnswer: value.text || '',
        timeTaken: 0,
      }));
      const response = await api.put(`/quiz/${quiz._id}/attempt/${attempt._id}`, {
        answers: submittedAnswers,
        timeSpent: Math.max(0, Math.round((Date.now() - startedAt) / 1000)),
      });
      const nextResult = response.data?.data || response.data;
      setResult(nextResult);
      setScholarships((previous) => previous.map((item) => (
        item._id === quiz._id
          ? { ...item, attemptStatus: 'completed', award: nextResult.scholarshipAward }
          : item
      )));
    } catch (error) {
      setMessage(error.response?.data?.message || 'Unable to submit the scholarship quiz.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <section
        className="student-scholarship-quizzes"
        aria-labelledby="student-scholarship-quizzes-title"
      >
        <div className="student-scholarship-heading">
          <div>
            <span><i className="bi bi-stars" /> Scholarship opportunity</span>
            <h2 id="student-scholarship-quizzes-title">Scholarship Tests</h2>
            <p>Attend a test and turn your score into a course discount.</p>
          </div>
          <strong>{scholarships.length} available</strong>
        </div>

        <div className="student-scholarship-grid">
          {scholarships.map((item) => {
            const itemCourseName = item.course?.courseName || item.course?.courseCode || 'Career course';
            const image = mediaUrl(item.thumbnailUrl || item.course?.thumbnailUrl || item.course?.promoImageUrl);
            const attemptsRemaining = Math.max(0, Number(item.maxAttempts || 1) - Number(item.attemptsUsed || 0));
            const completed = !!item.award && attemptsRemaining === 0;
            return (
              <article className="student-scholarship-card" key={item._id}>
                <div className="student-scholarship-art">
                  <div className="student-scholarship-art-fallback" aria-hidden="true">
                    <i className="bi bi-mortarboard-fill" />
                    <span><i className="bi bi-award-fill" /></span>
                  </div>
                  {image && <img src={image} alt={`${itemCourseName} scholarship test`} onError={(event) => { event.currentTarget.style.display = 'none'; }} />}
                  <span className="student-scholarship-badge"><i className="bi bi-stars" /> Scholarship</span>
                </div>
                <div className="student-scholarship-card-body">
                  <small>{itemCourseName}</small>
                  <h3>{item.title}</h3>
                  {item.description && <p>{item.description}</p>}
                  <div className="student-scholarship-meta">
                    <span><i className="bi bi-question-circle" /> {item.questionCount} questions</span>
                    <span><i className="bi bi-star" /> {item.totalMarks} marks</span>
                    {item.totalDuration > 0 && <span><i className="bi bi-clock" /> {item.totalDuration} min</span>}
                  </div>
                  {item.capacityReached ? (
                    <button type="button" className="student-scholarship-earned" disabled>
                      <i className="bi bi-people-fill" /> Participant Limit Reached
                    </button>
                  ) : completed ? (
                    <button type="button" className="student-scholarship-earned" disabled>
                      <i className="bi bi-award-fill" /> {item.award.discountPercentage}% Scholarship Earned
                    </button>
                  ) : (
                    <button type="button" className="student-scholarship-attend" onClick={() => openScholarship(item)}>
                      {item.award ? `Try Again (${attemptsRemaining} left)` : 'Attend the Test Now'} <i className="bi bi-arrow-right" />
                    </button>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {selected && (
        <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center p-3" style={{ zIndex: 1060, background: 'rgba(15,23,42,0.62)' }}>
          <div className="bg-white w-100 overflow-auto" style={{ maxWidth: 720, maxHeight: '92vh', borderRadius: 12, boxShadow: '0 24px 70px rgba(15,23,42,0.28)' }}>
            <div className="d-flex align-items-start justify-content-between gap-3 p-3 border-bottom">
              <div>
                <div className="fw-bold" style={{ color: '#0f172a' }}>{selected.title}</div>
                <small className="text-muted">Score {selected.totalMarks} marks to earn up to 100% course discount.</small>
              </div>
              <button type="button" className="btn btn-sm btn-light" onClick={closeDialog} aria-label="Close"><i className="bi bi-x-lg" /></button>
            </div>

            <div className="p-3 p-md-4">
              {message && <div className="alert alert-danger py-2">{message}</div>}

              {!quiz && !result && (
                <div>
                  <div className="p-3 mb-3" style={{ borderRadius: 10, background: '#f8fafc' }}>
                    <div className="fw-semibold mb-1">Discount course</div>
                    <div style={{ color: '#475569' }}>{courseName}</div>
                    <div className="d-flex flex-wrap gap-3 mt-2 text-muted" style={{ fontSize: 13 }}>
                      <span><i className="bi bi-question-circle me-1" />{selected.questionCount} questions</span>
                      <span><i className="bi bi-star me-1" />{selected.totalMarks} marks</span>
                      {selected.totalDuration > 0 && <span><i className="bi bi-clock me-1" />{selected.totalDuration} minutes</span>}
                    </div>
                  </div>
                  <button type="button" className="btn btn-primary w-100 fw-semibold" disabled={busy} onClick={startQuiz}>
                    {busy ? 'Starting...' : 'Start Scholarship Quiz'}
                  </button>
                </div>
              )}

              {quiz && !result && question && (
                <div>
                  <div className="d-flex justify-content-between align-items-center mb-2" style={{ fontSize: 13, color: '#64748b' }}>
                    <span>Question {questionIndex + 1} of {questions.length}</span>
                    <span>{question.marks} mark{question.marks === 1 ? '' : 's'}</span>
                  </div>
                  <div className="progress mb-4" style={{ height: 6 }}><div className="progress-bar" style={{ width: `${((questionIndex + 1) / questions.length) * 100}%` }} /></div>
                  <h6 className="fw-semibold mb-3" style={{ lineHeight: 1.5 }}>{question.text}</h6>
                  {(question.type === 'mcq' || question.type === 'true_false') ? (
                    <div className="d-flex flex-column gap-2">
                      {(question.options || []).map((option) => {
                        const selectedOption = answers[question._id]?.optionId === option._id;
                        return (
                          <button
                            key={option._id}
                            type="button"
                            className={`btn text-start ${selectedOption ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => setAnswers((previous) => ({ ...previous, [question._id]: { optionId: option._id } }))}
                          >
                            {option.text}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <input
                      className="form-control"
                      value={answers[question._id]?.text || ''}
                      onChange={(event) => setAnswers((previous) => ({ ...previous, [question._id]: { text: event.target.value } }))}
                      placeholder="Enter your answer"
                    />
                  )}
                  <div className="d-flex justify-content-between gap-2 mt-4">
                    <button type="button" className="btn btn-outline-secondary" disabled={questionIndex === 0} onClick={() => setQuestionIndex((value) => value - 1)}>Previous</button>
                    {questionIndex < questions.length - 1 ? (
                      <button type="button" className="btn btn-primary" onClick={() => setQuestionIndex((value) => value + 1)}>Next</button>
                    ) : (
                      <button type="button" className="btn btn-success" disabled={busy} onClick={submitQuiz}>{busy ? 'Submitting...' : 'Submit Quiz'}</button>
                    )}
                  </div>
                </div>
              )}

              {result && (
                <div className="text-center py-2">
                  <span className="d-inline-flex align-items-center justify-content-center mb-3" style={{ width: 64, height: 64, borderRadius: '50%', color: '#7c3aed', background: '#ede9fe' }}><i className="bi bi-award-fill" style={{ fontSize: 30 }} /></span>
                  <h4 className="fw-bold mb-1">{result.percentage}% Scholarship Earned</h4>
                  <p className="text-muted mb-3">Your {result.score}/{result.totalMarks} score gives you {result.scholarshipAward?.discountPercentage ?? result.percentage}% off {result.scholarshipAward?.courseId?.courseName || courseName}.</p>
                  <button type="button" className="btn btn-primary px-4" onClick={closeDialog}>Done</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
